import React, { useState, useEffect } from 'react';
import { Reorder } from 'framer-motion';

const CategoryDragDrop = ({ categories, onSave, onClose }) => {
  const [items, setItems] = useState([]);
  
  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [selectedL3, setSelectedL3] = useState(null);

  useEffect(() => {
    const clone = categories.map(c => ({ ...c }));
    clone.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setItems(clone);
  }, [categories]);

  const l1List = items.filter(c => c.level === 1);
  const l2List = selectedL1 ? items.filter(c => c.level === 2 && c.parent_id === selectedL1.id) : [];
  const l3List = selectedL2 ? items.filter(c => c.level === 3 && c.parent_id === selectedL2.id) : [];
  const l4List = selectedL3 ? items.filter(c => c.level === 4 && c.parent_id === selectedL3.id) : [];

  const handleReorder = (level, newOrderList) => {
    const newItems = [...items];
    newOrderList.forEach((item, index) => {
      const target = newItems.find(i => i.id === item.id);
      if (target) target.sort_order = index + 1;
    });
    newItems.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setItems(newItems);
  };

  const handleSaveAll = () => {
    const updates = items.map(i => ({ id: i.id, sort_order: i.sort_order }));
    onSave(updates);
    onClose();
  };

  const Column = ({ title, level, list, selectedItem, onSelect }) => (
    <div className="flex-1 flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner">
      <div className="p-4 bg-slate-800/80 border-b border-slate-700">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">{title}</h3>
        <p className="text-xs text-slate-400 mt-1">{list.length} item{list.length !== 1 && 's'}</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {list.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-xs italic">
            {level > 1 && !selectedItem ? "Select a parent first" : "No items found"}
          </div>
        ) : (
          <Reorder.Group axis="y" values={list} onReorder={(newOrder) => handleReorder(level, newOrder)} className="space-y-2">
            {list.map((item, index) => {
              const isSelected = selectedItem?.id === item.id;
              return (
                <Reorder.Item key={item.id} value={item}
                  className={`relative p-3 rounded-xl border cursor-grab active:cursor-grabbing transition-all select-none group flex flex-col gap-1.5 shadow-sm
                    ${isSelected ? 'bg-fuchsia-500/20 border-fuchsia-500 shadow-fuchsia-500/20' : 'bg-slate-800 border-slate-700 hover:border-fuchsia-500/50 hover:bg-slate-700/80'}`}
                  onClick={() => onSelect && onSelect(item)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0
                        ${isSelected ? 'bg-fuchsia-500 text-white' : 'bg-slate-900 text-slate-400'}`}>
                        {index + 1}
                      </div>
                      <h4 className={`font-bold text-sm truncate ${isSelected ? 'text-fuchsia-300' : 'text-slate-200'}`}>{item.name}</h4>
                    </div>
                    <div className="text-slate-500 opacity-0 group-hover:opacity-100 transition shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" /></svg>
                    </div>
                  </div>
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full shadow-xl flex flex-col overflow-hidden h-[700px]">
      <div className="p-6 border-b border-slate-800 bg-slate-800/50 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/20">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" /></svg>
          </div>
          <div>
            <h2 className="text-white font-black text-xl tracking-wide">Category  Reordering </h2>
            <p className="text-sm text-slate-400 mt-1">Drag and drop the cards to reorder. Click a card to view its children.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 px-5 rounded-xl transition text-sm">Cancel</button>
          <button onClick={handleSaveAll} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-2.5 px-6 rounded-xl transition text-sm flex items-center gap-2 shadow-lg shadow-emerald-500/20">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
            Save All Changes
          </button>
        </div>
      </div>
      <div className="flex-1 flex gap-4 p-6 bg-slate-950/30 overflow-hidden">
        <Column title="Level 1 (Main Section)" level={1} list={l1List} selectedItem={selectedL1} onSelect={(item) => { setSelectedL1(item); setSelectedL2(null); setSelectedL3(null); }} />
        <Column title="Level 2 (Category)" level={2} list={l2List} selectedItem={selectedL2} onSelect={(item) => { setSelectedL2(item); setSelectedL3(null); }} />
        <Column title="Level 3 (Sub Category)" level={3} list={l3List} selectedItem={selectedL3} onSelect={(item) => setSelectedL3(item)} />
        <Column title="Level 4 (Item)" level={4} list={l4List} />
      </div>
      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }`}</style>
    </div>
  );
};

export default CategoryDragDrop;
